
import { Injectable, signal, inject } from '@angular/core';
import { TranslationService } from './translation.service';
import { ErrorMappingService, FriendlyError } from './error-mapping.service';
import { Router } from '@angular/router';

export interface AppNotification {
    id: number;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    title?: string;
    suggestion?: string;
    actionLabel?: string;
    actionLink?: string;
    isTechnical?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private translationService = inject(TranslationService);
  private errorMappingService = inject(ErrorMappingService);
  // FIX: Explicitly typed the injected Router to resolve "Property 'navigate' does not exist on type 'unknown'".
  private router: Router = inject(Router);

  notifications = signal<AppNotification[]>([]);

  private incomingAlertAudio: HTMLAudioElement | null = null;

  handleError(error: any) {
    console.error('System Error Logged:', error);
    
    // Use the mapping service to convert raw error to friendly object
    const friendly = this.errorMappingService.mapError(error);
    
    this.addNotification({
        type: 'error',
        title: friendly.title,
        message: friendly.message,
        suggestion: friendly.suggestion,
        actionLabel: friendly.actionLabel,
        actionLink: friendly.actionLink,
        isTechnical: friendly.isTechnical
    });
  }

  showError(msg: string) {
    this.addNotification({ type: 'error', message: msg, title: 'ข้อผิดพลาด' });
  }

  showSuccess(msg: string) {
    this.addNotification({ type: 'success', message: msg, title: 'สำเร็จ' });
  }

  showInfo(msg: string) {
    this.addNotification({ type: 'info', message: msg });
  }

  showWarning(msg: string) {
    this.addNotification({ type: 'warning', message: msg, title: 'คำเตือน' });
  }

  private addNotification(n: Omit<AppNotification, 'id'>) {
    const id = Date.now();
    // Default timeout depends on type (Errors stay longer)
    const timeout = n.type === 'error' ? 8000 : 5000;
    
    this.notifications.update(prev => [...prev, { id, ...n }]);
    setTimeout(() => this.remove(id), timeout);
  }

  remove(id: number) {
    this.notifications.update(prev => prev.filter(n => n.id !== id));
  }
  
  // Method to handle action clicks from notification component
  handleAction(notification: AppNotification) {
      if (notification.actionLink) {
          this.router.navigate([notification.actionLink]);
          this.remove(notification.id);
      }
  }
  
  private async safePlay(url: string, isLoop: boolean = false, volume: number = 0.6): Promise<HTMLAudioElement | null> {
    try {
      const audio = new Audio(url);
      audio.volume = volume;
      audio.loop = isLoop;
      
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Audio play was prevented by browser.', error);
        });
      }
      
      return audio;
    } catch (e) {
      console.warn('Audio system failed to initialize');
      return null;
    }
  }

  playSuccessSound() {
    this.safePlay('https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3');
  }

  playQueuePing() {
    this.safePlay('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3', false, 0.8);
  }

  async playIncomingAlert() {
    this.stopIncomingAlert();
    const audio = await this.safePlay('https://assets.mixkit.co/active_storage/sfx/1359/1359-preview.mp3', true, 0.7);
    if (audio) {
      this.incomingAlertAudio = audio;
    }
  }

  stopIncomingAlert() {
    if (this.incomingAlertAudio) {
      try {
        this.incomingAlertAudio.pause();
        this.incomingAlertAudio.currentTime = 0;
      } catch (e) {}
      this.incomingAlertAudio = null;
    }
  }
}
